// src/oura/ask/answers.ts
//
// Ask Oura (PREVIEW): a deterministic intent matcher on keywords (no model),
// and one builder per intent. Three jobs, in this order:
//   1. Guide staff on Oura (guide.ts): how do I, where is, what does it mean.
//   3. Prepare key work: start the right document in Oura's own Create a doc,
//      the approved answers as page changes, the next step on Delivery.
//   2. Answer from the client's own records: catch up, performance, who owes
//      what; on Businesses, what needs me today, what is late, who is quiet.
// Every builder reads the records the existing screens read (useAskData.ts).
// An answer opens with one or two plain sentences, numbers in them, each
// followed by a citation that links to the screen that shows the fact.
// Sentence case, no em dashes. Nothing here writes, sends or spends.

import { actorText, type Deliverable } from "@service/aeo/delivery";
import { getBusiness } from "../data";
import { planPageHref } from "./planHref";
import { dayMonth } from "../visibility/format";
import type { AllData, ClientData } from "./useAskData";
import { samplePasteOnly } from "@service/aeo/publishRefusal";
import { createPrefillOf } from "../delivery/DocStepParts";
import { DOC_TYPES, docTypeIn, guideAnswer, guideGroups, guideMiss, isGuideCue, startText, type GuideCtx } from "./guide";
import { andList, closest, contactOf, hrefs, n, p, s, src, visSource, wd } from "./text";
import type { AskAnswer, AskBlock, AskCtx, AskGo, AskRow, AskScope, AskSentence, AskTableRow, ProposalPreview } from "./types";
import type { AgreedSet } from "./agreed";
import { originLabel, planProposals, planPageUrl } from "./planProposals";
import { isGoneQuiet, triageCount, triageGroups } from "./triage";
import { FACT_POOL, factAnswer } from "./facts";

/* ------------------------------------------------------------------------- *
 * Starters
 * ------------------------------------------------------------------------- */

/** A starter card on a new chat: the question it sends and one quiet line under it. */
export interface AskStarter {
    prompt: string;
    hint: string;
}

/**
 * The starters lead with the use cases: what needs me (triage), how the
 * client is doing on AI, and moving the client's approved answers. How do I
 * stays last.
 */
export function startersFor(scope: AskScope, d?: Pick<ClientData, "agreed" | "requests"> | null): AskStarter[] {
    if (scope.kind === "all") {
        return [
            { prompt: "What needs me today?", hint: "Late, waiting on us, ready for a yes, quiet" },
            { prompt: "What's waiting on us longest?", hint: "The oldest reply or request we owe" },
            { prompt: "Who has gone quiet?", hint: "No answer to our documents" },
            { prompt: "How do I…?", hint: "Buttons and screens in Oura" },
        ];
    }
    const agreed = d?.agreed ?? null;
    return [
        { prompt: "What needs me today?", hint: "Late, waiting on us, ready for a yes" },
        { prompt: "How is this client doing on AI?", hint: "Per engine, the last scan, who is named instead" },
        agreed
            ? { prompt: "Start the page drafts", hint: `${agreed.clientName} approved ${n(agreed.answers.length, "answer")}` }
            : { prompt: "Catch me up", hint: "Plan, AI visibility, who owes what" },
        { prompt: "How do I…?", hint: "Buttons and screens in Oura" },
    ];
}

/* ------------------------------------------------------------------------- *
 * Intents
 * ------------------------------------------------------------------------- */

export type ClientIntent = "catchup" | "ours" | "waiting" | "changed" | "performance" | "rivals" | "fallback";
export type AllIntent = "today" | "late" | "waiting" | "quiet" | "fallback";

const has = (t: string, re: RegExp) => re.test(t);

/** Who is named instead: rivals, competitors, who beats them. */
const RIVALS = /\b(competitors?|competition|rivals?|named instead|who (else )?(gets|is|are|was|were) (being )?named|who else|beating|beats?|outrank\w*|ahead of (them|us|her|him)|winning over|taking (their|our) (spot|place))\b/;
/** How they do in AI answers. */
const PERFORMANCE = /\b(doing (on|in) ai|ai answers?|in ai\b|on ai\b|performance|performing|visibility|visible|show(s|ing)? up|per engine|each engine|engines?|chatgpt|gemini|google's ai|ai search|ai assistants?)\b/;

/** Catch me up, in the many ways staff ask it: "what's the latest", "any updates", "what's going on". */
const CATCHUP = /\b(catch|summary|summari[sz]e|recap|brief me|status|updates?|update me|overview|where are we|where (do )?things stand|how is|how are|how's|latest|news|what's new|anything new|what is new|happening|happened|going on|up to|progress|how (are )?things)\b/;

export function clientIntent(text: string, contact?: string | null): ClientIntent {
    const t = text.toLowerCase().replace(/[’]/g, "'");
    if (has(t, /\b(changed?|moved?|movement|since the last|difference|trend|went up|went down|dropped)\b/)) return "changed";
    if (has(t, /\b(waiting on us|on us|our side|needs? us|with us|we owe|late|overdue|behind)\b/)) return "ours";
    // The client's side, in staff's own words: "what is Siew Ling sitting on", "what does she owe us", "since when".
    const who = contact && contact !== "the client" ? contact.toLowerCase().split(/\s+/)[0] : null;
    const theirs = /\b(sitting on|holding( up)?|owes?|owing|since when|stuck on|blocked on|yet to|hasn't|has not|not (yet )?(replied|answered|sent)|outstanding|pending)\b/;
    if (has(t, /\b(waiting on|client owes|on the client|their side|waiting)\b/) || ((who ? t.includes(who) : /\b(the client|client|she|he|they)\b/.test(t)) && has(t, theirs))) return "waiting";
    if (has(t, RIVALS)) return has(t, PERFORMANCE) || /\bhow (is|are|'s)\b/.test(t) ? "performance" : "rivals";
    if (has(t, PERFORMANCE)) return "performance";
    if (has(t, CATCHUP)) return "catchup";
    return "fallback";
}

export function allIntent(text: string): AllIntent {
    const t = text.toLowerCase().replace(/[’]/g, "'");
    if (has(t, /\b(quiet|silent|not replied|no reply|haven't replied|gone cold)\b/)) return "quiet";
    if (has(t, /\b(late|overdue|behind|came back)\b/)) return "late";
    if (has(t, /\b(waiting on (the )?clients?|waiting)\b/)) return "waiting";
    if (has(t, /\b(today|needs? me|need us|needs us|what now|to do|todo|catch|summary|status|latest|news|updates?|what's new|anything new|going on|happening)\b/)) return "today";
    return "fallback";
}

/* ------------------------------------------------------------------------- *
 * Client scope: job 2, the records
 * ------------------------------------------------------------------------- */

const current = (ds: Deliverable[]) => ds.filter((d) => d.group !== "later" && d.group !== "done");

function lateCount(d: ClientData): number {
    return d.delivery ? d.delivery.deliverables.filter((x) => x.actor?.late).length : 0;
}

export { planPageHref };

/** The quiet line under a new chat: approved answers that can become page changes. */
export function noticeFor(d: ClientData): string | null {
    if (!d.agreed) return null;
    return `${n(d.agreed.answers.length, "approved answer")} can become page changes`;
}

function catchUp(d: ClientData): AskAnswer {
    const id = d.businessId;
    const pl = d.delivery;
    const delivery = src("Delivery", hrefs.delivery(id));
    const contact = contactOf(d);
    const first: AskSentence[] = [];
    if (pl) {
        const late = lateCount(d);
        const stage = pl.phase === "not_started" ? `${d.name} has not started yet` : `${d.name} is in month ${pl.month} of ${pl.termMonths}`;
        first.push(s(`${stage}, with ${pl.doneCount.done} of ${pl.doneCount.total} deliverables done${late ? ` and ${late} late` : " and nothing late"}.`, delivery));
    } else {
        first.push(s(`${d.name} has no Delivery plan in Oura yet.`, delivery));
    }
    if (d.vis) {
        const by = d.vis.engines.filter((e) => e.label !== "Google's AI").map((e) => `${e.label} ${e.named}`);
        first.push(s(`AI names them in ${d.vis.named} of ${d.vis.of} questions${by.length ? ` (${by.join(", ")})` : ""}, from the ${dayMonth(d.vis.capturedAt)} scan.`, visSource(d)));
    } else if (d.snapshot && d.snapshot.total !== null) {
        first.push(s(`Their score is ${d.snapshot.total}, from the ${dayMonth(d.snapshot.capturedAt)} scan.`, src("Overview", hrefs.overview(id))));
    }

    const groups = pl ? current(pl.deliverables) : [];
    const ourSteps = groups.filter((x) => x.group === "needs_us").length;
    const theirSteps = groups.filter((x) => x.group === "waiting_client").length;
    const c = d.board?.counts;
    const openReq = (d.requests ?? []).filter((r) => r.status === "open").length;
    const us = [pl ? n(ourSteps, "next step") : null, c ? `${n(c.needsUs, "document")} to answer` : null, openReq ? n(openReq, "open request") : null].filter((x): x is string => Boolean(x));
    const them = [pl && theirSteps ? n(theirSteps, "next step") : null, c ? n(c.waitingOnClient, "document") : null].filter((x): x is string => Boolean(x));
    const second: AskSentence[] = [];
    if (us.length) second.push(s(`On us: ${andList(us)}.`, delivery));
    if (them.length) second.push(s(`On ${contact}: ${andList(them)}.`, src("Client tab", hrefs.client(id))));
    const blocks = [p(...first)];
    if (second.length) blocks.push(p(...second));
    return { blocks, followUps: ["What's waiting on us?", `What's waiting on ${contact}?`, "What changed since the last scan?"] };
}

/** Ours: the steps with us, late and came-back first. */
function oursRanked(d: ClientData): Deliverable[] {
    const ours = d.delivery ? current(d.delivery.deliverables).filter((x) => x.group === "needs_us") : [];
    const rank = (x: Deliverable) => (x.actor?.cameBack ? 0 : x.actor?.late ? 1 : 2);
    return ours.sort((a, b) => rank(a) - rank(b) || (a.due ?? "").localeCompare(b.due ?? ""));
}

function oursAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const pl = d.delivery;
    const delivery = src("Delivery", hrefs.delivery(id));
    if (!pl) return { blocks: [p(s("There is no Delivery plan in Oura yet, so nothing is tracked as ours.", delivery))], followUps: ["Catch me up"] };
    const ours = oursRanked(d);
    const late = ours.filter((x) => x.actor?.late || x.actor?.cameBack);
    const lateIn = (g: Deliverable["group"]) => pl.deliverables.filter((x) => x.actor?.late && x.group === g).length;
    const openReq = (d.requests ?? []).filter((r) => r.status === "open");
    const rows: AskRow[] = ours.map((x) => ({
        title: `${x.code} ${x.label}`,
        sub: x.next?.text,
        meta: x.actor?.cameBack ? "Came back" : x.actor?.late ? `Due ${x.actor.when}` : actorText(x.actor),
        late: Boolean(x.actor?.late || x.actor?.cameBack),
        to: hrefs.deliverable(id, x.key),
    }));
    const lead =
        ours.length === 0
            ? s("Nothing on Delivery waits on us.", delivery)
            : late.length === 0
                ? s(`${n(ours.length, "step")} ${ours.length === 1 ? "is" : "are"} with us, all on time.`, delivery)
                : s(`${n(ours.length, "step")} ${ours.length === 1 ? "is" : "are"} with us, and ${late.length} of them ${late.length === 1 ? "is" : "are"} late or came back.`, delivery);
    const sentences = [lead];
    if (openReq.length) sentences.push(s(`${contactOf(d)} also has ${n(openReq.length, "open request")} waiting on a reply.`, src("Client tab", hrefs.client(id))));
    const blocks: AskBlock[] = [p(...sentences)];
    if (rows.length) blocks.push({ kind: "list", title: "With us, late first", tone: "blue", rows });
    const elsewhere = [
        lateIn("waiting_client") ? `${lateIn("waiting_client")} waiting on the client` : null,
        lateIn("checking") ? `${lateIn("checking")} in Checking` : null,
        lateIn("later") ? `${lateIn("later")} in Later` : null,
    ].filter((x): x is string => Boolean(x));
    if (elsewhere.length) blocks.push(p(s(`Also late, not with us: ${andList(elsewhere)}.`, delivery)));
    return { blocks, followUps: ["What should I do next?", `What's waiting on ${contactOf(d)}?`, "What do the Delivery statuses mean?"] };
}

/** Working days in a Delivery row's "Client · 6 days" (deliveryRules actorOf counts working days), so the chat and the row agree. */
const actorDays = (when: string | undefined): number | null => {
    const m = when ? /^(\d+) days?$/.exec(when) : null;
    return m ? Number(m[1]) : null;
};

/**
 * Everything waiting on the client, in one list, longest first: the documents
 * waiting on them, the Delivery steps waiting on them, and the questions we
 * asked them inside docs whose own wait is ours. Every age is in working days.
 */
function waitingAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const contact = contactOf(d);
    const clientTab = src("Client tab", hrefs.client(id));
    const waits = d.delivery?.documentWaits ?? [];
    type Wait = AskRow & { days: number; kind: "doc" | "step" | "question" };
    const rows: Wait[] = [];
    for (const x of (d.board?.documents ?? []).filter((y) => y.waitingOn === "client")) {
        const ask = waits.find((w) => w.docId === x.id)?.question;
        const days = x.waitingWorkingDays ?? 0;
        rows.push({
            kind: "doc",
            days,
            title: x.pageTitle?.split("\n")[0] ?? x.docType ?? x.name,
            sub: ask ?? (x.questions ? `${n(x.questions.waitingOnClient, "question")} to answer` : undefined),
            meta: [wd(days), x.late ? "late" : null].filter(Boolean).join(", "),
            late: Boolean(x.late),
            to: hrefs.doc(id, x.id),
        });
    }
    for (const x of d.delivery ? current(d.delivery.deliverables).filter((y) => y.group === "waiting_client") : []) {
        const days = actorDays(x.actor?.when) ?? 0;
        rows.push({
            kind: "step",
            days,
            title: `${x.code} ${x.label}`,
            sub: x.items.find((i) => i.waitingOn === "client" && i.clientAsk)?.clientAsk ?? x.clientLine,
            meta: [wd(days), x.actor?.late ? "late" : null].filter(Boolean).join(", "),
            late: Boolean(x.actor?.late),
            to: hrefs.deliverable(id, x.key),
        });
    }
    for (const q of d.clientQuestions ?? []) {
        rows.push({
            kind: "question",
            days: q.days,
            title: q.text.length > 90 ? `${q.text.slice(0, 88).trimEnd()}…` : q.text,
            sub: `Question in the ${q.docType}`,
            meta: [wd(q.days), q.late ? "late" : null].filter(Boolean).join(", "),
            late: q.late,
            to: hrefs.doc(id, q.docId),
        });
    }
    if (rows.length === 0) {
        return { blocks: [p(s(`Nothing waits on ${contact} right now.`, clientTab))], followUps: ["What's waiting on us?", "Catch me up"] };
    }
    // Longest first; on a tie, late first.
    rows.sort((a, b) => b.days - a.days || Number(b.late) - Number(a.late));
    const count = (k: Wait["kind"], one: string) => {
        const c = rows.filter((r) => r.kind === k).length;
        return c ? n(c, one) : null;
    };
    const parts = [count("doc", "document"), count("step", "deliverable"), count("question", "open question")].filter((x): x is string => Boolean(x));
    const top = rows[0];
    const sentences = [s(`${contact} has ${andList(parts)} to come back on.`, clientTab)];
    const name = top.kind === "question" ? `the question “${top.title.replace(/[.?!…]+$/, "")}”${top.sub ? ` (${top.sub.replace(/^Question in the /, "in the ")})` : ""}` : top.title;
    if (top.days > 0) sentences.push(s(`The longest is ${name}, waiting ${wd(top.days)}.`));
    const blocks: AskBlock[] = [
        p(...sentences),
        { kind: "list", title: `Waiting on ${contact}, longest first`, tone: "amber", rows: rows.map((r) => ({ title: r.title, sub: r.sub, meta: r.meta, late: r.late, to: r.to })) },
    ];
    return { blocks, followUps: ["What's waiting on us?", "How do I send this week's note?"] };
}

function changedAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const v = d.vis;
    const history = src("History", hrefs.history(id));
    const after = ["How is this client doing on AI?", "How do I start a rescan?"];
    if (v) {
        const all = v.moves.find((m) => m.label === "All engines");
        if (!all) return { blocks: [p(s("There is only one scan so far, so there is no change to show yet.", history))], followUps: after };
        if (!all.comparable) return { blocks: [p(s("The questions changed between the last two scans, so the two results do not compare.", visSource(d)))], followUps: after };
        const of = all.of ?? v.of;
        const verb = all.value > 0 ? `AI names them in ${n(all.value, "more question")}` : all.value < 0 ? `AI names them in ${n(-all.value, "fewer question")}` : "AI names them in the same number of questions";
        const word = (x: number) => (x > 0 ? `Up ${x}` : x < 0 ? `Down ${-x}` : "No change");
        const rows: AskTableRow[] = v.moves.filter((x) => x.label !== "All engines" && x.comparable).map((m) => ({ label: m.label, value: `${word(m.value)}, now ${m.now}`, cite: visSource(d) }));
        for (const e of v.firstTime) rows.push({ label: e, value: "First scan, nothing to compare" });
        const blocks: AskBlock[] = [p(s(`Since ${dayMonth(all.since)}, ${verb}, now ${all.now} of ${of}.`, visSource(d)), s(`That compares the ${dayMonth(all.since)} and ${dayMonth(v.capturedAt)} scans.`, history))];
        if (rows.length) blocks.push({ kind: "table", rows });
        return { blocks, followUps: after };
    }
    const sn = d.snapshot;
    if (sn && sn.total !== null && sn.previousTotal !== null) {
        // Equal totals are no change, never "up".
        const change = sn.total === sn.previousTotal ? `no change from ${sn.previousTotal}` : `${sn.total > sn.previousTotal ? "up" : "down"} from ${sn.previousTotal}`;
        return { blocks: [p(s(`Their score is ${sn.total}, ${change} on the scan before.`, src("Overview", hrefs.overview(id))))], followUps: after };
    }
    return { blocks: [p(s("There is only one scan so far, so there is no change to show yet.", history))], followUps: after };
}

/** Performance: AI visibility per engine, the change since the last scan, and who is named instead. */
function performanceAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const v = d.vis;
    if (!v) return { blocks: [p(s(`There is no AI visibility scan for ${d.name} in Oura yet.`, src("History", hrefs.history(id))))], followUps: ["How do I start a rescan?"] };
    const cite = visSource(d);
    const all = v.moves.find((m) => m.label === "All engines");
    const sentences = [s(`AI names ${d.name} in ${v.named} of ${v.of} questions on the ${dayMonth(v.capturedAt)} scan.`, cite)];
    if (all && all.comparable && all.since) {
        sentences.push(s(all.value === 0 ? `That is the same as on ${dayMonth(all.since)}.` : `That is ${all.value > 0 ? "up" : "down"} ${Math.abs(all.value)} since ${dayMonth(all.since)}.`, src("History", hrefs.history(id))));
    }
    const top = v.rivals.slice(0, 3);
    if (top.length) {
        sentences.push(s(`Named instead most often: ${andList(top.map((r) => `${r.name} on ${n(r.takes, "question")}`))}.`, src("Visibility · Competitors", hrefs.competitors(id))));
    }
    const word = (m: (typeof v.moves)[number] | undefined) => (m && m.comparable ? (m.value > 0 ? `, up ${m.value}` : m.value < 0 ? `, down ${-m.value}` : ", no change") : "");
    const rows: AskTableRow[] = v.engines.map((e) => ({ label: e.label, value: `${e.named} of ${e.of}${word(v.moves.find((m) => m.label === e.label))}`, cite }));
    return {
        blocks: [p(...sentences), { kind: "table", rows }],
        followUps: ["What do the engine rings mean?", "Where do I see who is named instead?", "What changed since the last scan?"],
    };
}

/** Who is named instead of the client, most first: the Competitors tab's own list. */
function rivalsAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const v = d.vis;
    if (!v) return { blocks: [p(s(`There is no AI visibility scan for ${d.name} in Oura yet, so there is no list of who is named instead.`, src("History", hrefs.history(id))))], followUps: ["How do I start a rescan?"] };
    const cite = src("Visibility · Competitors", hrefs.competitors(id));
    const top = v.rivals.slice(0, 5);
    if (!top.length) return { blocks: [p(s(`On the ${dayMonth(v.capturedAt)} scan, no other business is named where ${d.name} is not.`, cite))], followUps: ["How is this client doing on AI?"] };
    const blocks: AskBlock[] = [
        p(
            s(`On the ${dayMonth(v.capturedAt)} scan, AI names ${n(v.rivals.length, "other business", "other businesses")} on questions where it does not name ${d.name}.`, cite),
            s(`${top[0].name} is named instead most often, on ${n(top[0].takes, "question")}.`),
        ),
        {
            kind: "list",
            title: "Named instead, most first",
            tone: "gray",
            rows: top.map((r) => ({ title: r.name, sub: r.fresh ? `${n(r.fresh, "new question")} since the scan before` : undefined, meta: n(r.takes, "question"), to: hrefs.competitors(id) })),
        },
    ];
    return { blocks, followUps: ["How is this client doing on AI?", "What changed since the last scan?"] };
}

/* ------------------------------------------------------------------------- *
 * Client scope: job 3, prepare the work
 * ------------------------------------------------------------------------- */

const isStart = (t: string) => /\b(start|create|make|begin|draft|write|prepare|set up|new)\b/.test(t);

/** Start the right document: Oura's own Create a doc, with the template picked. Never written in the chat. */
function startDocAnswer(d: ClientData, docType: string, asked: string): AskAnswer {
    const id = d.businessId;
    const sentences: AskSentence[] = [];
    if (/\b(write|draft)\b/.test(asked)) sentences.push(s("Ask Oura does not write documents in the chat. It opens Oura's own Create a doc with the right template."));
    const step = (d.delivery?.deliverables ?? []).find((x) => x.docStep?.docType === docType && x.group !== "done");
    const existing = (d.board?.documents ?? []).filter((x) => x.docType === docType).sort((a, b) => (b.modifiedAt ?? "").localeCompare(a.modifiedAt ?? ""))[0];
    if (step) {
        // The date as the Delivery row shows it (the actor), so the chat and the row agree.
        const when = step.actor?.when;
        const timing = step.actor?.late ? `, late since ${when}` : when ? `, due ${when}` : "";
        sentences.push(s(`The ${docType} is the next step on ${step.code} ${step.label}${timing}.`, src(`Delivery · ${step.code}`, hrefs.deliverable(id, step.key))));
    } else if (existing) {
        sentences.push(s(`There is already ${/^[AEIOU]/.test(docType) ? "an" : "a"} ${docType}. Create a doc opens it.`, src(docType, hrefs.doc(id, existing.id))));
    }
    const blocks: AskBlock[] = [];
    if (sentences.length) blocks.push(p(...sentences));
    // The codes Delivery's own Create the doc would prefill for this step, never the doc type's whole set.
    const pre = step ? createPrefillOf(step, step.mainAction.kind === "create_doc" ? step.mainAction : null) : null;
    blocks.push({ kind: "start", docType, text: startText(docType), to: hrefs.create(id, docType, pre?.codes ?? [], pre?.outlet ?? null) });
    const others = DOC_TYPES.filter((x) => x !== docType && x !== "GBP Pack").map((x) => `Start the ${x}`);
    return { blocks, followUps: [...others.slice(0, 2), "How do I share a doc with the client?"] };
}

/** Suggest the next step on Delivery: ours, came back or late first, then the earliest due. */
function nextAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const delivery = src("Delivery", hrefs.delivery(id));
    if (!d.delivery) return { blocks: [p(s("There is no Delivery plan in Oura yet, so there is no next step to suggest.", delivery))], followUps: ["Catch me up"] };
    const ours = oursRanked(d);
    const top = ours[0];
    if (!top) return { blocks: [p(s("Nothing on Delivery waits on us. The rest waits on the client or on Oura's re-check.", delivery))], followUps: [`What's waiting on ${contactOf(d)}?`] };
    const why = top.actor?.cameBack ? "it came back: the latest scan found the problem again" : top.actor?.late ? `it is late, due ${top.actor.when}` : top.actor ? `it is next, ${actorText(top.actor)}` : "it is next";
    const sentences = [s(`Next: ${top.code} ${top.label}. ${top.next?.text ? `${top.next.text}. ` : ""}It comes first because ${why}.`, src(`Delivery · ${top.code}`, hrefs.deliverable(id, top.key)))];
    if (d.agreed) sentences.push(s(`Also: ${noticeFor(d)}.`, src(d.agreed.docTitle, hrefs.doc(id, d.agreed.docId))));
    const blocks: AskBlock[] = [p(...sentences), { kind: "go", go: { label: `Open ${top.code}`, to: hrefs.deliverable(id, top.key) } }];
    const after = ours.slice(1, 4).map((x) => ({
        title: `${x.code} ${x.label}`,
        sub: x.next?.text,
        meta: x.actor?.cameBack ? "Came back" : x.actor?.late ? `Due ${x.actor.when}` : actorText(x.actor),
        late: Boolean(x.actor?.late || x.actor?.cameBack),
        to: hrefs.deliverable(id, x.key),
    }));
    if (after.length) blocks.push({ kind: "list", title: "Then", tone: "blue", rows: after });
    return { blocks, followUps: d.agreed ? ["Start the page drafts", "What's waiting on us?"] : ["What's waiting on us?", "Start the monthly performance report"] };
}

const DRAFTS = /\b(page drafts?|start (the )?drafts|approved answers?|page changes?|website changes?|site changes?|changes (on|to) the (site|website|homepage|page))\b|\bapproved\b.*\banswers?\b|\bturn (them|these|those|it|the answers?) into\b/;

function prepareAnswer(t: string, d: ClientData): AskAnswer | null {
    const type = docTypeIn(t);
    if (type && (DOC_TYPES as readonly string[]).includes(type) && isStart(t)) return startDocAnswer(d, type, t);
    if (DRAFTS.test(t)) return draftsAnswer(d);
    if (/\b(next step|what next|what happens next|what comes next|do next|what should (i|we) do|suggest)\b/.test(t)) return nextAnswer(d);
    return null;
}

/**
 * Is Oura drafting work for this question (page drafts, or a document started in Create a doc)?
 * Decided by the answer once it is built, by the question while the records are still being read.
 */
export function isDrafting(text: string, answer?: AskAnswer | null): boolean {
    if (answer) return answer.blocks.some((b) => b.kind === "proposals" || b.kind === "start");
    const t = text.toLowerCase().replace(/[’]/g, "'");
    if (/\b(how|where|what does)\b/.test(t)) return false;
    const type = docTypeIn(t);
    return DRAFTS.test(t) || (!!type && (DOC_TYPES as readonly string[]).includes(type) && isStart(t));
}

/* ------------------------------------------------------------------------- *
 * The use cases
 * ------------------------------------------------------------------------- */

const isTriage = (t: string) => /\b(needs? me|need us|needs us|what now|to do|todo|today|triage|morning)\b/.test(t);
const isLongest = (t: string) => /\b(longest|oldest|waited (the )?longest|waiting (the )?longest)\b/.test(t);
// Only questions about a change going live, or about after the yes. A plain "What happens next?" is the next step on Delivery.
const isWentLive = (t: string) => /\b(went live|gone live|go live|goes live|is (it )?live|it live|see it live|live yet|after (the|my) yes)\b/.test(t);

/**
 * A read the answer needs failed: say so and offer a retry, and state no fact
 * from the part that did load. `whose` is "Nyonya Table's" or "your clients'".
 */
export function loadFailedAnswer(whose: string): AskAnswer {
    return { blocks: [p(s(`Couldn't load ${whose} records. Try again.`)), { kind: "retry", label: "Try again" }], followUps: [] };
}

const whoseRecords = (only: { name: string } | null) => (only ? `${only.name}'s` : "your clients'");

/** Use case 1: what needs me, across every client or one. */
export function triageAnswer(a: AllData, only: { id: string; name: string } | null): AskAnswer {
    // Triage reads Delivery (today) and the documents (across): either missing means unknown, never "nothing needs you".
    if (a.error || !a.today || !a.across) return loadFailedAnswer(whoseRecords(only));
    const groups = triageGroups(a, only?.id ?? null).filter((g) => g.rows.length > 0);
    const { things, clients } = triageCount(groups);
    const cites = only ? [src("Delivery", hrefs.delivery(only.id)), src("Client tab", hrefs.client(only.id))] : [src("Today", hrefs.today()), src("Documents (all clients)", hrefs.allDocs())];
    if (things === 0) {
        return { blocks: [p(s(only ? `Nothing needs you on ${only.name} today.` : "Nothing needs you across your clients today.", ...cites))], followUps: ["How do I…?"] };
    }
    const lead = only ? `${n(things, "thing needs", "things need")} you on ${only.name}.` : `${n(things, "thing needs", "things need")} you across ${n(clients, "client")}.`;
    // Late on Delivery speaks in Delivery's own unit ("6 deliverables late, 1 came back"); the rest by row.
    const head = groups.find((g) => g.lead);
    const parts = groups.filter((g) => !g.lead).map((g) => `${g.rows.length} ${g.title.charAt(0).toLowerCase()}${g.title.slice(1)}`);
    const rest = parts.length ? `${head ? "Then " : ""}${andList(parts)}, most urgent first.` : "Most urgent first.";
    const blocks: AskBlock[] = [p(s(lead, ...cites), ...(head?.lead ? [s(head.lead)] : []), s(rest))];
    blocks.push({ kind: "triage", groups });
    if (!only && a.today?.notSet?.length) {
        blocks.push(p(s(`${andList(a.today.notSet.map((x) => x.businessName))} ${a.today.notSet.length === 1 ? "has" : "have"} no Delivery plan in Oura yet, so nothing of theirs can be late.`, src("Today", hrefs.today()))));
    }
    // The client with the most rows, for "Only …".
    const byClient = new Map<string, { name: string; count: number }>();
    for (const r of groups.flatMap((g) => g.rows)) byClient.set(r.businessId, { name: r.client, count: (byClient.get(r.businessId)?.count ?? 0) + 1 });
    const top = Array.from(byClient.values()).sort((x, y) => y.count - x.count)[0];
    const followUps = only
        ? ["What's waiting on us longest?", "How is this client doing on AI?"]
        : [...(top && clients > 1 ? [`Only ${top.name}`] : []), "What's waiting on us longest?", "Who has gone quiet?"];
    return { blocks, followUps };
}

/** "What's waiting on us longest?": the oldest reply or request we owe. */
export function longestAnswer(a: AllData, only: { id: string; name: string } | null): AskAnswer {
    if (a.error || !a.today || !a.across) return loadFailedAnswer(whoseRecords(only));
    const us = triageGroups(a, only?.id ?? null).find((g) => g.key === "us")?.rows ?? [];
    if (!us.length) return { blocks: [p(s("Nothing waits on us: no doc reply and no open request.", src("Documents (all clients)", hrefs.allDocs())))], followUps: ["What needs me today?"] };
    const [top, ...rest] = us;
    const cite = src(`${top.client} · ${top.go.label === "Open the request" ? "Request" : "Document"}`, top.go.to);
    const blocks: AskBlock[] = [
        p(s(`${top.client} has waited longest on us: ${top.age.replace(/^Asked /, "asked ")}.`, cite), s(top.text)),
        { kind: "go", go: top.go },
    ];
    const then: AskRow[] = rest.slice(0, 3).map((r) => ({ title: r.client, sub: r.text, meta: r.age, late: r.late, to: r.go.to }));
    if (then.length) blocks.push({ kind: "list", title: "Next longest", tone: "blue", rows: then });
    return { blocks, followUps: only ? ["What needs me today?"] : ["What needs me today?", "Who has gone quiet?"] };
}

/** Use case 3, step 1: the approved answers, before and after, and the way into the Page workshop. */
function draftsAnswer(d: ClientData): AskAnswer {
    const a = d.agreed;
    if (!a) return { blocks: [p(s("No approved answers are waiting to become page changes.", src("Delivery", hrefs.delivery(d.businessId))))], followUps: ["What should I do next?"] };
    const k = a.answers.length;
    const domain = getBusiness(d.businessId)?.domain;
    // planProposals only maps the sample's own plan (no real page mapping exists yet). A real client gets null here.
    const sample = samplePasteOnly(d.businessId) !== undefined;
    const plan = domain ? planProposals(a, planPageUrl(domain), sample) : null;
    // Only a mapping may show a before/after. Without one the question is not the homepage's text, so say so and stop.
    const mapped = plan !== null && plan.recs.length === k && a.answers.every((x, i) => plan.recs[i]?.after === x.answer && !!plan.recs[i]?.before);
    if (!plan || !mapped) {
        return {
            blocks: [
                p(
                    s(`I found ${n(k, "approved answer")} in the ${a.docTitle}, but I could not match ${k === 1 ? "it" : "them"} to the homepage's current wording, so I cannot show a before and after.`, src(a.docTitle, hrefs.doc(d.businessId, a.docId))),
                    s("Open the Answer Plan and the Page workshop to review the wording before any draft starts."),
                ),
                { kind: "links", links: [{ go: { label: `Open the ${a.docTitle}`, to: hrefs.doc(d.businessId, a.docId) }, text: "The approved answers, as the client agreed them." }, { go: { label: "Open the homepage in Page workshop", to: planPageHref(d.businessId, false) }, text: "The page as Oura last read it." }] },
            ],
            followUps: ["What should I do next?"],
        };
    }
    const items: ProposalPreview[] = a.answers.map((x, i) => {
        const rec = plan.recs[i];
        return { question: x.question, before: rec.before, after: rec.after, approved: `${x.by}, ${dayMonth(x.at)}` };
    });
    const days = Array.from(new Set(a.answers.map((x) => dayMonth(x.at))));
    return {
        blocks: [
            p(
                s(`${a.clientName} approved ${n(k, "answer")} in the ${a.docTitle}, on ${andList(days)}.`, src(a.docTitle, hrefs.doc(d.businessId, a.docId))),
                s(`No page draft has started from ${k === 1 ? "it" : "them"} yet. The "Now" wording is the sample Answer Plan's own question, not text read from the live homepage. It shows what would change.`),
            ),
            { kind: "proposals", origin: originLabel(a), items },
            {
                kind: "suggest",
                text: `${k === 1 ? "It waits" : k === 2 ? "Both wait" : `All ${k} wait`} on the homepage in the Page workshop, marked ${originLabel(a)}. Nothing changes on the site until a named person says yes there.`,
                to: planPageHref(d.businessId),
            },
        ],
        followUps: ["Where do I see it went live?", `What's waiting on ${a.clientName}?`],
    };
}

/**
 * Use case 3: "Where do I see it went live?". Oura does not read the page again
 * in this build, so these screens cannot confirm a change on their own. The
 * copy says so and points at the live site instead.
 */
function wentLiveAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const a = d.agreed;
    const links: { go: AskGo; text: string }[] = [
        { go: { label: "Open the homepage in Page workshop", to: planPageHref(id, false) }, text: "The page as Oura last read it, with the time of that read. View page opens the live site, so you can check the wording yourself." },
        { go: { label: "Open Delivery", to: hrefs.delivery(id) }, text: a ? "The Start the page drafts card does not close on its own in this build." : "Steps in Delivery do not move to Done on their own in this build." },
    ];
    if (a) links.push({ go: { label: `Open the ${a.docTitle}`, to: hrefs.doc(id, a.docId) }, text: `${a.clientName}'s ${n(a.answers.length, "approved answer")}. This build does not fill in the Live since reply on its own.` });
    return {
        blocks: [p(s("This build cannot confirm that a change went live. Oura does not read the page again yet, so these screens do not update when the wording changes. To check, use View page in the Page workshop to open the live site.", src("Delivery", hrefs.delivery(id))))]
            .concat([{ kind: "links", links }]),
        followUps: ["How do I get Oura to re-check a fix?", "What needs me today?"],
    };
}

/** What the workshop's yes did: the site a person pastes on (the yes dialog's own word), and whether the copy worked. */
export interface YesResult {
    /** "Wix": the workshop's paste-only site, from the same source as the yes dialog. null: the workshop publishes. */
    pasteOn: string | null;
    /** The browser let the workshop copy the new wording. Ignored when `pasteOn` is null. */
    copied: boolean;
}

/**
 * Use case 3, after the yes: posted into the chat by the Page workshop when a
 * named person says yes to the approved changes. Steps 3 and 4 are a labelled
 * preview: the re-read that closes the step is not wired in this build. The
 * site's name and the copy result come from the workshop, so the chat says
 * what the dialog and the workshop said.
 */
export function afterYesAnswer(businessId: string, a: AgreedSet, actor: string, count: number, at: Date, result: YesResult): AskAnswer {
    const time = at.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true }).replace(" ", "").toLowerCase();
    const days = Array.from(new Set(a.answers.map((x) => dayMonth(x.at))));
    const site = result.pasteOn;
    const it = count === 1 ? "it" : "them";
    const how = site
        ? result.copied
            ? s(`The new wording is on your clipboard, ready to paste on ${site}.`)
            : s(`Copying did not work in this browser. Select and copy the new wording on the right, then paste ${it} on ${site}.`)
        : s(`The workshop's own Publish confirm is next. Nothing changes on the site until ${actor} presses Publish there.`);
    const second: NextStepIn = site
        ? {
              title: `Paste on ${site}`,
              text: result.copied ? `Oura cannot write to a ${site} site, so a person pastes the new wording in.` : `Oura cannot write to a ${site} site. Select and copy the new wording on the right in the Page workshop, then paste ${it} in.`,
          }
        : { title: "Publish in the Page workshop", text: "The workshop's Publish confirm writes the change to the site." };
    return {
        blocks: [
            p(s(`${actor} said yes to ${n(count, "change")} on the homepage, from ${a.clientName}'s approved answers.`, src("Page workshop · Homepage", planPageHref(businessId, false))), how),
            {
                kind: "next",
                note: "Preview: Oura's re-read is not connected in this build. Steps 3 and 4 show what it will do.",
                steps: [
                    { title: "Your yes", text: `${actor}, today at ${time}. ${a.clientName} approved the wording in the ${a.docTitle} on ${andList(days)}.`, state: "done" },
                    { ...second, state: "now", go: { label: "Open the Page workshop", to: planPageHref(businessId, false) } },
                    { title: "Oura reads the homepage again", text: `Not connected in this build. Once it is, Oura will look for ${n(count, "new passage")} at its next check.`, state: "preview", go: { label: "Open Delivery", to: hrefs.delivery(businessId) } },
                    { title: "The step closes on proof", text: `Not connected in this build. Once it is, Oura will check the ${count === 1 ? "passage" : "passages"} on the page and update Delivery and the ${a.docTitle}.`, state: "preview", go: { label: `Open the ${a.docTitle}`, to: hrefs.doc(businessId, a.docId) } },
                ],
            },
        ],
        followUps: ["Where do I see it went live?", "What needs me today?"],
    };
}

type NextStepIn = { title: string; text: string };

/* ------------------------------------------------------------------------- *
 * What Ask Oura will not do: one specific line each
 * ------------------------------------------------------------------------- */

const ASKED = "^(?:(?:can|could|would|will) you |please |pls |go ahead and |just |i want you to |i need you to )*";
const SEND = new RegExp(`${ASKED}(send|e-?mail|message|text|whatsapp|ping|reply to|write to|contact)\\b`);
const PUBLISH = new RegExp(`${ASKED}(publish|push|put)\\b|\\bpublish\\b(?!ed).*\\bfor (me|us)\\b`);
const UNPUBLISH = /\b(unpublish|take (it |the [\w ]+ )?(offline|down))\b/;
const DELETE = /\b(delete|remove|trash|get rid of|erase)\b/;

/** The thing the request names ("the menu page"), so the refusal names it back. */
function objectOf(t: string, verb: RegExp): string | null {
    const m = new RegExp(`${verb.source}\\s+(.{2,48}?)\\s*(?:for (?:me|us)|now|please|today)?\\s*[?.!]*$`).exec(t);
    const o = m?.[m.length - 1]?.trim();
    return o ? o.replace(/^(it|them)$/, "that") : null;
}

/**
 * Asked to send, publish, unpublish or delete: one line that names the action,
 * why Ask Oura does not do it, and the screen where a person does, when Oura
 * has one. Questions about how to do it are the guide's, not refusals.
 */
function refusal(t: string, d: ClientData | null): AskAnswer | null {
    if (/^\s*(how|where|what|when|why|who|which|is|are|does|did)\b/.test(t)) return null;
    const id = d?.businessId ?? null;
    const contact = d ? contactOf(d) : "the client";
    const done = (sentence: AskSentence, go?: AskGo): AskAnswer => ({ blocks: [p(sentence), ...(go ? [{ kind: "go", go } as AskBlock] : [])], followUps: [] });
    if (UNPUBLISH.test(t)) {
        const what = objectOf(t, /\b(?:unpublish|take)/) ?? "a page";
        return done(s(`Ask Oura never unpublishes or deletes anything, so it will not unpublish ${what.replace(/\s*(offline|down)$/, "")}. Oura has no screen that takes a page down: a person does it on the client's own site.`));
    }
    if (DELETE.test(t)) {
        const what = objectOf(t, /\b(?:delete|remove|trash|get rid of|erase)/) ?? "that";
        return done(s(`Ask Oura never deletes anything, so it will not delete ${what}. Oura has no screen that deletes a page or a doc: a person does it on the client's own site or in their folder.`));
    }
    if (SEND.test(t)) {
        const open = (d?.requests ?? []).filter((r) => r.status === "open").sort((a, b) => b.at.localeCompare(a.at))[0];
        const go: AskGo | undefined = id ? (open ? { label: "Open the request", to: hrefs.request(id, open.id) } : { label: "Open this week's note", to: hrefs.note(id) }) : { label: "Open Businesses", to: hrefs.businesses() };
        const where = open ? `from ${contact}'s open request on the Client tab, with Send reply` : `from This week's note on the Client tab, with Send`;
        return done(s(`Ask Oura does not send emails or messages, so it will not write to ${contact}. A person sends them in Oura, ${id ? where : "from the client's Client tab"}.`), go);
    }
    if (PUBLISH.test(t)) {
        const what = objectOf(t, /\b(?:publish|push|put)/)?.replace(/\s+live$/, "") ?? "the changes";
        const site = id ? samplePasteOnly(id) : undefined;
        const how = site ? `say yes there and get the copy to paste on ${site}` : "say yes there and press Publish";
        const go: AskGo | undefined = id ? { label: d?.agreed ? "Open the homepage in Page workshop" : "Open the Page workshop", to: d?.agreed ? planPageHref(id, false) : hrefs.pages(id) } : undefined;
        return done(s(`Ask Oura does not publish, so it will not publish ${what}. A named person opens the Page workshop to ${how}.`), go);
    }
    return null;
}

/* ------------------------------------------------------------------------- *
 * Did you mean
 * ------------------------------------------------------------------------- */

/**
 * Not sure: "Did you mean…" with the two closest answers as buttons. The full
 * list of what Ask Oura does is offered once per conversation, as one chip.
 */
function didYouMean(t: string, pool: string[], ctx: AskCtx, lead = "I am not sure what you mean."): AskAnswer {
    return {
        blocks: [p(s(`${lead} Did you mean one of these?`)), { kind: "choices", items: closest(t, pool) }],
        followUps: ctx.menuShown ? [] : ["What can you do?"],
        // Oura's AI step may answer instead, where it is on (live.ts).
        unsure: true,
    };
}

const isMenuAsk = (t: string) => /^\s*(what can you do|what do you do|help|menu|options|what can i ask( you)?|everything you can do)\W*$/.test(t);

function clientPool(d: ClientData): string[] {
    const contact = contactOf(d);
    const guide = guideGroups({ businessId: d.businessId, name: d.name, d }).flatMap((g) => g.items);
    return [
        "What needs me today?",
        "How is this client doing on AI?",
        "Who is named instead?",
        `What's waiting on ${contact}?`,
        "What's waiting on us?",
        "Catch me up",
        "What changed since the last scan?",
        "Start the page drafts",
        "Start the monthly performance report",
        "Start the monthly work snapshot",
        "What should I do next?",
        ...FACT_POOL,
        ...guide,
    ];
}

/* ------------------------------------------------------------------------- *
 * The client router
 * ------------------------------------------------------------------------- */

function clientMenu(d: ClientData): AskAnswer {
    return {
        blocks: [
            p(s(`I can help with three things for ${d.name}. Ask Oura reads and links: it does not send, spend or mark anything done.`)),
            {
                kind: "topics",
                groups: [
                    { title: "Guide you on Oura", items: ["How do I start a rescan?", "Where is the Answer Plan?", d.vis ? `What does ${d.vis.named} of ${d.vis.of} mean?` : "What do the Delivery statuses mean?"] },
                    { title: `Answer from ${d.name}'s records`, items: ["Catch me up", "How is this client doing on AI?", "What's waiting on us?"] },
                    { title: "Prepare the work", items: ["Start the monthly performance report", "Start the page drafts", "What should I do next?"] },
                ],
            },
        ],
        followUps: [],
    };
}

const NO_CTX: AskCtx = { menuShown: false };

export function clientAnswer(text: string, d: ClientData, all?: AllData | null, ctx: AskCtx = NO_CTX): AskAnswer {
    const t = text.toLowerCase().replace(/[’]/g, "'").trim();
    const only = { id: d.businessId, name: d.name };
    // A failed read: no answer from the records that did load.
    if (d.error) return loadFailedAnswer(whoseRecords(only));
    if (isMenuAsk(t)) return ctx.menuShown ? didYouMean(t, clientPool(d), ctx, "The full list is earlier in this chat.") : clientMenu(d);
    const no = refusal(t, d);
    if (no) return no;
    if (isWentLive(t)) return wentLiveAnswer(d);
    if (all && isLongest(t)) return longestAnswer(all, only);
    if (all && isTriage(t) && !/\bhow\b|\bwhere\b/.test(t)) return triageAnswer(all, only);
    const gctx: GuideCtx = { businessId: d.businessId, name: d.name, d };
    const guide = guideAnswer(t, gctx, ctx);
    if (guide) return guide;
    const prep = prepareAnswer(t, d);
    if (prep) return prep;
    // The facts another screen shows (score, dates, questions, Google, site, plan, people, setup).
    const fact = factAnswer(t, d);
    if (fact) return fact;
    switch (clientIntent(t, contactOf(d))) {
        case "catchup":
            return catchUp(d);
        case "ours":
            return oursAnswer(d);
        case "waiting":
            return waitingAnswer(d);
        case "changed":
            return changedAnswer(d);
        case "performance":
            return performanceAnswer(d);
        case "rivals":
            return rivalsAnswer(d);
        default:
            return isGuideCue(t) ? guideMiss(gctx, t) : didYouMean(t, clientPool(d), ctx);
    }
}

/* ------------------------------------------------------------------------- *
 * All clients (the Businesses page)
 * ------------------------------------------------------------------------- */

function todayAnswer(a: AllData, only?: "late" | "waiting"): AskAnswer {
    // "waiting" reads only the documents; every other view needs Today as well. A missing slice is unknown, not empty.
    if (!a.across || (only !== "waiting" && !a.today)) return loadFailedAnswer(whoseRecords(null));
    const rows = a.today?.rows ?? [];
    const band = (r: (typeof rows)[number]) => r.band ?? (r.actor.cameBack || r.actor.late ? "today" : "coming");
    const late = rows.filter((r) => r.actor.late || r.actor.cameBack);
    const now = rows.filter((r) => !r.actor.late && !r.actor.cameBack && band(r) === "today");
    const coming = rows.filter((r) => !r.actor.late && !r.actor.cameBack && band(r) === "coming");
    const waits = a.across?.waitingOnClient ?? [];
    const clients = new Set([...late, ...now].map((r) => r.businessId)).size;
    const today = src("Today", hrefs.today());
    const docs = src("Documents (all clients)", hrefs.allDocs());
    const toRow = (r: (typeof rows)[number]): AskRow => ({
        title: `${r.businessName} · ${r.code} ${r.label}`,
        sub: r.next.text,
        meta: r.actor.cameBack ? "Came back" : r.actor.late ? `Due ${r.actor.when}` : actorText(r.actor),
        late: r.actor.late || r.actor.cameBack,
        to: hrefs.deliverable(r.businessId, r.deliverableKey),
    });
    const waitRows: AskRow[] = waits.map((w) => ({
        title: `${w.businessName} · ${w.title}`,
        sub: w.lastClientPost ? `Last from them: ${w.lastClientPost.text}` : w.questions ? `${n(w.questions.out - w.questions.back, "question")} unanswered` : undefined,
        meta: [wd(w.waitingWorkingDays), w.lateWorkingDays ? `${n(w.lateWorkingDays, "day")} late` : null].filter(Boolean).join(", "),
        late: Boolean(w.lateWorkingDays),
        to: hrefs.doc(w.businessId, w.documentId),
    }));
    const blocks: AskBlock[] = [];
    if (only === "waiting") {
        const sentences = [s(`${n(waits.length, "document")} ${waits.length === 1 ? "waits" : "wait"} on clients.`, docs)];
        const longest = Math.max(0, ...waits.map((w) => w.waitingWorkingDays ?? 0));
        if (longest > 0) sentences.push(s(`The longest has waited ${wd(longest)}.`));
        blocks.push(p(...sentences));
        if (waitRows.length) blocks.push({ kind: "list", title: "Waiting on clients", tone: "amber", rows: waitRows });
        return { blocks, followUps: ["Who has gone quiet?", "What's late?"] };
    }
    if (only === "late") {
        blocks.push(p(s(late.length ? `${n(late.length, "step is", "steps are")} late or came back to us.` : "Nothing is late across your clients.", today)));
        if (late.length) blocks.push({ kind: "list", title: "Late or came back", tone: "blue", rows: late.map(toRow) });
        return { blocks, followUps: ["What needs me today?", "Who has gone quiet?"] };
    }
    const total = now.length + late.length;
    blocks.push(
        p(
            s(`${n(total, "step needs", "steps need")} you today across ${n(clients, "client")}${late.length ? `, and ${late.length} of them ${late.length === 1 ? "is" : "are"} late or came back` : ""}.`, today),
            s(`${n(waits.length, "document")} ${waits.length === 1 ? "waits" : "wait"} on clients.`, docs),
        ),
    );
    if (late.length) blocks.push({ kind: "list", title: "Late or came back", tone: "blue", rows: late.map(toRow) });
    if (now.length) blocks.push({ kind: "list", title: "Due today", tone: "blue", rows: now.map(toRow), more: coming.length ? `${n(coming.length, "more step")} coming up.` : undefined });
    if (a.today?.notSet?.length) blocks.push(p(s(`No Delivery yet for ${andList(a.today.notSet.map((x) => x.businessName))}.`)));
    return { blocks, followUps: ["What's late?", "Who has gone quiet?"] };
}

/**
 * Quiet: the client has answered none of the documents we sent them, the
 * same definition as the triage's Gone quiet group (isGoneQuiet). A client we
 * have sent nothing to is not quiet. From the documents read across clients.
 */
function quietAnswer(a: AllData): AskAnswer {
    if (!a.across) return loadFailedAnswer(whoseRecords(null));
    const clients = a.across.clients;
    const docs = src("Documents (all clients)", hrefs.allDocs());
    const waitsBy = new Map<string, number>();
    for (const w of a.across.waitingOnClient) waitsBy.set(w.businessId, Math.max(waitsBy.get(w.businessId) ?? 0, w.waitingWorkingDays ?? 0));
    const quiet = clients.filter(isGoneQuiet);
    quiet.sort((x, y) => (waitsBy.get(y.businessId) ?? 0) - (waitsBy.get(x.businessId) ?? 0));
    const followUps = ["What does Quiet mean?", "What needs me today?"];
    if (!quiet.length) return { blocks: [p(s("No client is quiet. Every client we have sent documents to has answered at least one.", docs))], followUps };
    const rows: AskRow[] = quiet.map((c) => {
        const longest = waitsBy.get(c.businessId) ?? 0;
        return {
            title: c.name,
            sub: `No reply to ${n(c.replyBasis.sent, "document")} we sent`,
            meta: longest ? (wd(longest) ?? undefined) : undefined,
            late: c.late > 0,
            to: hrefs.client(c.businessId),
        };
    });
    return {
        blocks: [p(s(`${n(quiet.length, "client")} ${quiet.length === 1 ? "has" : "have"} not answered a document yet.`, docs)), { kind: "list", title: "Quiet clients", tone: "amber", rows }],
        followUps,
    };
}

function allMenu(): AskAnswer {
    return {
        blocks: [
            p(s("Across clients I can help with three things. Ask Oura reads and links: it does not send, spend or mark anything done.")),
            {
                kind: "topics",
                groups: [
                    { title: "Guide you on Oura", items: ["How do I start a new scan?", "Where do I see what is due today?", "What does Quiet mean?"] },
                    { title: "Answer from every client's records", items: ["What needs me today?", "What's late?", "Who has gone quiet?", "What's waiting on clients?"] },
                ],
            },
            p(s("To prepare the work, open a client and ask there: start a document in Create a doc, start the page drafts, or get the next step on Delivery.")),
        ],
        followUps: [],
    };
}

type Named = { kind: "one"; id: string; name: string } | { kind: "ambiguous"; names: string[] };

const normName = (x: string) => x.toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim();
const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Where `name` sits in `t` as whole words only: "AI" never matches inside "waiting", "Art" never inside "start". */
function nameSpans(t: string, name: string): [number, number][] {
    const re = new RegExp(`(^|[^\\p{L}\\p{N}])(${escapeRe(name)})(?=$|[^\\p{L}\\p{N}])`, "gu");
    const out: [number, number][] = [];
    for (let m = re.exec(t); m; m = re.exec(t)) {
        const at = m.index + m[1].length;
        out.push([at, at + m[2].length]);
        re.lastIndex = at + 1;
    }
    return out;
}

/**
 * The client a question names ("Only Nyonya Table"), from the clients the
 * records list, matched on whole words. The longest name wins where one sits
 * inside another ("Art House" over "Art"). Two different clients named, or two
 * clients with the same name, is ambiguous: the answer asks which, never picks.
 */
function namedClient(t: string, a: AllData): Named | null {
    const all = new Map<string, string>();
    for (const c of a.across?.clients ?? []) all.set(c.businessId, c.name);
    for (const r of a.today?.rows ?? []) all.set(r.businessId, r.businessName);
    const text = normName(t);
    const hits = Array.from(all.entries())
        .map(([id, name]) => ({ id, name, spans: normName(name) ? nameSpans(text, normName(name)) : [] }))
        .filter((h) => h.spans.length > 0);
    // A name only ever found inside a longer client's name is that longer name, not a second client.
    const inside = (x: [number, number], y: [number, number]) => y[0] <= x[0] && x[1] <= y[1] && y[1] - y[0] > x[1] - x[0];
    const kept = hits.filter((h) => !h.spans.every((sp) => hits.some((o) => o !== h && o.spans.some((os) => inside(sp, os)))));
    if (kept.length === 0) return null;
    if (kept.length === 1) return { kind: "one", id: kept[0].id, name: kept[0].name };
    return { kind: "ambiguous", names: Array.from(new Set(kept.map((h) => h.name))) };
}

/** More than one client fits the question: ask which, and pick none. */
function whichClient(names: string[], longest: boolean): AskAnswer {
    if (names.length === 1) {
        return {
            blocks: [p(s(`More than one client is called ${names[0]}, so I cannot tell which one you mean. Open that client and ask there.`, src("Businesses", hrefs.businesses())))],
            followUps: ["What needs me today?"],
        };
    }
    return {
        blocks: [p(s(`That names ${andList(names)}. Which client do you mean?`)), { kind: "choices", items: names.map((x) => (longest ? `What's waiting on us longest for ${x}?` : `Only ${x}`)) }],
        followUps: [],
    };
}

const ALL_POOL = ["What needs me today?", "What's late?", "Who has gone quiet?", "What's waiting on clients?", "What's waiting on us longest?", "How do I start a new scan?", "Where do I see what is due today?", "What does Quiet mean?"];

export function allAnswer(text: string, a: AllData, ctx: AskCtx = NO_CTX): AskAnswer {
    const t = text.toLowerCase().replace(/[’]/g, "'").trim();
    // A failed read: no answer from the records that did load.
    if (a.error) return loadFailedAnswer(whoseRecords(null));
    if (isMenuAsk(t)) return ctx.menuShown ? didYouMean(t, ALL_POOL, ctx, "The full list is earlier in this chat.") : allMenu();
    const no = refusal(t, null);
    if (no) return no;
    const named = namedClient(t, a);
    const only = named?.kind === "one" ? { id: named.id, name: named.name } : null;
    // Client scope exists for two operations only: the "Only …" / today triage and the longest wait.
    const namedTriage = /^only\b/.test(t) || (isTriage(t) && !/\bhow\b|\bwhere\b/.test(t));
    if (named?.kind === "ambiguous" && (isLongest(t) || namedTriage)) return whichClient(named.names, isLongest(t));
    if (isLongest(t)) return longestAnswer(a, only);
    if (only && namedTriage) return triageAnswer(a, only);
    if (!named && isTriage(t) && !/\bhow\b|\bwhere\b/.test(t)) return triageAnswer(a, null);
    const gctx: GuideCtx = { businessId: null, name: null, d: null };
    const guide = guideAnswer(t, gctx, ctx);
    if (guide) return guide;
    // Any other question that names a client is not answered from the all-clients records, which would change its meaning.
    if (only) {
        return didYouMean(
            t,
            [`Only ${only.name}`, `What's waiting on us longest for ${only.name}?`],
            ctx,
            `From here I can answer two things about ${only.name}: what needs us today, and who has waited longest on us. Open the client for anything else.`,
        );
    }
    if (named?.kind === "ambiguous") return whichClient(named.names, false);
    switch (allIntent(t)) {
        case "today":
            return todayAnswer(a);
        case "late":
            return todayAnswer(a, "late");
        case "waiting":
            return todayAnswer(a, "waiting");
        case "quiet":
            return quietAnswer(a);
        default:
            return isGuideCue(t) ? guideMiss(gctx, t) : didYouMean(t, ALL_POOL, ctx);
    }
}

/** Plain text of an answer, for Copy. Citations become nothing; tables, steps and lists become lines. */
export function answerText(answer: AskAnswer): string {
    const out: string[] = [];
    for (const b of answer.blocks) {
        if (b.kind === "p") out.push(b.sentences.map((x) => x.text).join(" "));
        else if (b.kind === "table") out.push(b.rows.map((r) => `${r.label}: ${r.value}`).join("\n"));
        else if (b.kind === "list") out.push([`${b.title}`, ...b.rows.map((r) => `- ${r.title}${r.meta ? ` (${r.meta})` : ""}`)].join("\n"));
        else if (b.kind === "steps") out.push(b.steps.map((x, i) => `${i + 1}. ${x}`).join("\n"));
        else if (b.kind === "topics") out.push(b.groups.map((g) => [g.title, ...g.items.map((x) => `- ${x}`)].join("\n")).join("\n\n"));
        else if (b.kind === "suggest" || b.kind === "start") out.push(b.text);
        else if (b.kind === "triage") out.push(b.groups.map((g) => [`${g.title} (${g.rows.length})`, ...g.rows.map((r) => `- ${r.client}: ${r.text} (${r.age})`)].join("\n")).join("\n\n"));
        else if (b.kind === "proposals") out.push(b.items.map((x, i) => `${i + 1}. Now: ${x.before}\n   New: ${x.after}\n   Approved by ${x.approved}`).join("\n"));
        else if (b.kind === "next") out.push([...b.steps.map((x, i) => `${i + 1}. ${x.title}: ${x.text}`), b.note].join("\n"));
        else if (b.kind === "links") out.push(b.links.map((l) => `- ${l.go.label}: ${l.text}`).join("\n"));
        else if (b.kind === "choices") out.push(b.items.map((x) => `- ${x}`).join("\n"));
        else if (b.kind === "retry") out.push(b.label);
        else if (b.kind === "action") out.push(`${b.label}: ${b.says}`);
    }
    return out.join("\n\n");
}
